import { Box, Button, CircularProgress } from "@mui/material";
import { useFormikContext } from "formik";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { ReactElement, ReactNode } from "react";
import { useFormContext } from "react-hook-form";

export default function SaveButtons({
  handleSave,
  isSaving,
  isDisabled,
  note,
  variant = "saveAndContinue",
}: {
  handleSave: (opts: { goToNext: boolean }) => any;
  isSaving?: boolean;
  isDisabled?: boolean;
  note?: ReactNode;
  variant: "saveAndContinue" | "continueOnly";
}): ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "center",
      }}
      columnGap={1}
    >
      {note}
      {isSaving ? <CircularProgress size={24} /> : null}
      {variant !== "continueOnly" && (
        <Button
          variant="contained"
          color="tertiary"
          disabled={isSaving || isDisabled}
          onClick={() => handleSave({ goToNext: false })}
        >
          Save
        </Button>
      )}
      <Button
        variant="contained"
        color="primary"
        disabled={isSaving || (isDisabled && variant === "saveAndContinue")}
        onClick={() => handleSave({ goToNext: true })}
      >
        {variant === "continueOnly" ? "Continue" : "Save And Continue"}
      </Button>
    </Box>
  );
}

export function FormikSaveButtons({
  continueWithoutSaving,
  confirmSubmit,
  disabled,
  note,
}: {
  continueWithoutSaving: () => any;
  confirmSubmit?: ConfirmOptions | null;
  disabled?: boolean;
  note?: ReactNode;
}): ReactElement {
  const formik = useFormikContext<{ goToNext?: boolean }>();
  const showConfirm = useConfirm();

  return (
    <SaveButtons
      handleSave={async ({ goToNext }) => {
        if (formik.dirty) {
          if (confirmSubmit) {
            const { confirmed } = await showConfirm(confirmSubmit);
            if (!confirmed) {
              return;
            }
          }
          formik.setFieldValue("goToNext", goToNext).catch(console.error);
          await formik.submitForm();
        } else {
          continueWithoutSaving();
        }
      }}
      isSaving={formik.isSubmitting}
      isDisabled={
        formik.isSubmitting || !!Object.keys(formik.errors).length || disabled
      }
      note={note}
      variant={formik.dirty ? "saveAndContinue" : "continueOnly"}
    />
  );
}

export function RhfSaveButtons({
  continueWithoutSaving,
  confirmSubmit,
  note,
  onSubmit,
}: {
  continueWithoutSaving: () => any;
  confirmSubmit?: ConfirmOptions | null;
  note?: ReactNode;
  onSubmit: () => Promise<void>;
}): ReactElement {
  const showConfirm = useConfirm();
  const {
    formState: { isDirty, isSubmitting },
    setValue,
  } = useFormContext();

  return (
    <SaveButtons
      handleSave={async ({ goToNext }) => {
        if (isDirty) {
          if (confirmSubmit) {
            const { confirmed } = await showConfirm(confirmSubmit);
            if (!confirmed) {
              return;
            }
          }
          setValue("goToNext", goToNext);
          await onSubmit();
        } else {
          continueWithoutSaving();
        }
      }}
      isSaving={isSubmitting}
      note={note}
      variant={isDirty ? "saveAndContinue" : "continueOnly"}
    />
  );
}
